import { useAppSelector } from "@app/hooks";
import { RootState } from "@app/store";
import { PropertyManifestEntry } from "@features/home-manifest/types";
import { useGetPropertyManifestQuery } from "@features/home/api";
import { ManifestDevice } from "@features/plan-manifest/types";
import { Category } from "@lib/labels";
import { formatDate } from "@lib/utils";
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack, Box, Button,
} from "@mui/material";
import {useSnackbar} from "notistack";
import {SnackType} from "@lib/types";
import {getErrorMessage} from "@lib/utils";
import ExpandableSnack from "@features/Common/ExpandableSnack";
import {useCallback, useEffect, useMemo, useState} from "react";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {SmartstartPairingDialog} from "@features/provisioning/components/provision-review/SmartstartPairingDialog";
import {useCreateSmartstartEntryMutation} from "@features/provisioning/api";
import {SmartstartParams} from "@features/provisioning/types";

const Review = () => {
  const { enqueueSnackbar } = useSnackbar();

  const property = useAppSelector((state: RootState) => state.property);
  const userTenant = useAppSelector((state: RootState) => state.userTenant);
  const selectedTenant = useAppSelector((state: RootState) => state.tenant);

  const currentTenantId = useMemo(() => (selectedTenant.currentTenant || userTenant)?.tenant_id || '', [selectedTenant.currentTenant, userTenant]);

  const {
    refetch: refetchManifest,
    currentData: propertyManifest
  } = useGetPropertyManifestQuery({
    tenantId: currentTenantId,
    propertyId: property?.property_id || '',
    includeEntries: true,
  }, {
    skip: !currentTenantId || !property?.property_id,
  });

  const manifestEntries = useMemo(() => [
    ...propertyManifest?.manifest_entries || [],
  ].sort((a, b) => {
    if (!Boolean(a.provisioned)) return -1;
    if (!Boolean(b.provisioned)) return 1;
    return Number(b.provisioned || 0) - Number(a.provisioned || 0);
  }), [propertyManifest?.manifest_entries]);

  /* Refetch manifest on mount */
  useEffect(() => {
    refetchManifest();
  }, [refetchManifest]);

  /* Formats provisioned timestamp*/
  const getProvisionDateLabel = (entry: PropertyManifestEntry) => {
    if (entry.provisioned) {
      return formatDate(entry.provisioned).fromNow();
    }
    return '';
  }

  /** Displays entry label with area and provisioned timestamp */
  const getDeviceLabel = (entry: PropertyManifestEntry) => {
    const device = entry.device as ManifestDevice;
    const name = device.friendly_name || device?.description || '';
    const area = entry.property_area;
    return `${name} in ${area}`;
  }

  const unpairedDevices = useMemo(() => manifestEntries
    .filter((e => !(e.device as ManifestDevice)?.sensors
      ?.some(s => s.sensor_category === Category.system)))
    .filter((e => !e.provisioned)) || [], [manifestEntries]);

  const smartstartDevicesToPair = useMemo(() => unpairedDevices
    .filter((e => !!e.dsk)) || [], [unpairedDevices]);

  const devicesToPairManually = unpairedDevices.filter(d => !d.dsk);

  const unpairedMessage = useMemo(() => [
    `There ${devicesToPairManually.length === 1 ? 'is' : 'are'}`,
    devicesToPairManually.length,
    `device${devicesToPairManually.length % 10 === 1 ? '' : 's'} that `,
    `need${devicesToPairManually.length === 1 ? 's' : ''} to be paired manually.`,
    'Return to the previous step to continue pairing devices.',
  ].join(' '), [devicesToPairManually.length]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [createSmartstartEntry] = useCreateSmartstartEntryMutation();

  const handleOpenDialog = useCallback(async () => {
    setIsDialogOpen(true);

    const result = await createSmartstartEntry({
      userTenantId: currentTenantId,
      propertyId: property?.property_id,
      devices: smartstartDevicesToPair.map(device => ({
        qr_code_string: device.dsk,
        device_name: device.manifest_entry_id,
        property_area: device.property_area
      } as SmartstartParams))
    });

    if ('error' in result && result.error) {
      enqueueSnackbar("Couldn't send codes to gateway:", {
        key: 'smartstart-error',
        content: (
          <ExpandableSnack
            id="smartstart-error"
            message={"Couldn't send codes to gateway:"}
            variant={SnackType.error}
            detail={getErrorMessage(result.error)}
          />
        ),
      });
      return;
    }

    enqueueSnackbar('QR code data sent to gateway', {
      variant: 'success',
    });
  }, [createSmartstartEntry, currentTenantId, property?.property_id, smartstartDevicesToPair, enqueueSnackbar]);

  const handleCloseDialog = () => setIsDialogOpen(false);

  return (
    <>
    <Stack spacing={2} sx={{ width: '100%'}}>
      <Box component={"div"} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {unpairedDevices?.length === 0 ? (
          <Alert severity="success">All devices are paired.</Alert>
        ) : (
          <>
            {devicesToPairManually?.length > 0 && (
              <Alert severity="warning">
                {unpairedMessage}
              </Alert>
            )}

            {smartstartDevicesToPair?.length > 0 && (
              <Alert severity="warning">
                There {smartstartDevicesToPair.length === 1 ? 'is' : 'are'}{' '}
                {smartstartDevicesToPair.length} Smartstart device
                {smartstartDevicesToPair.length % 10 === 1 ? '' : 's'}{' '}
                that need{smartstartDevicesToPair.length === 1 ? 's' : ''} to be paired.
                Please bring your Gateway online and{' '}
                <Button
                  onClick={handleOpenDialog}
                  sx={{
                    textTransform: 'none',
                    padding: '0 4px',
                    minWidth: 'auto',
                    textDecoration: 'underline',
                  }}
                >
                  click here
                </Button>{' '}
                to start Smartstart pairing
              </Alert>
            )}
          </>
        )}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 400 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Paired</TableCell>
              <TableCell>Smartstart</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Provisioned</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {manifestEntries
              .filter((e => !(e.device as ManifestDevice)?.sensors
                ?.some(s => s.sensor_category === Category.system)))
              .map((entry, index) => (
                <TableRow role="checkbox" key={index} tabIndex={-1}>
                  <TableCell sx={{ position: 'static' }}>
                    {Boolean(entry.provisioned)
                      // eslint-disable-next-line react/jsx-no-undef
                      ? <CheckBoxIcon color="success" />
                      : <CheckBoxOutlineBlankIcon color="disabled" />
                    }
                  </TableCell>
                  <TableCell>
                    {Boolean(entry.dsk)
                      ? <CheckBoxIcon color="primary"/>
                      : <CheckBoxOutlineBlankIcon color="disabled" />
                    }
                  </TableCell>
                  <TableCell>{getDeviceLabel(entry)}</TableCell>
                  <TableCell>{getProvisionDateLabel(entry)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>
    </Stack>
    <SmartstartPairingDialog
      isOpen={isDialogOpen}
      onClose={handleCloseDialog}
      smartstartDevicesToPair={smartstartDevicesToPair}
    />
  </>
  );
}

export default Review;